
<template>
  <div class="login-page">
    <!-- 1920px 设计版心 -->
    <main class="login-canvas">
      <!-- 页面背景层：第二张图片 -->
      <div class="login-background" aria-hidden="true"></div>

      <!-- 左侧科技图形：第一张图片 -->
      <div class="login-visual" aria-hidden="true">
        <div class="visual-glow"></div>
        <div class="visual-art"></div>
      </div>

      <!-- 右侧登录表单 -->
      <section class="login-panel">
        <div class="login-content">
          <h1 class="login-title">{{ systemName }}</h1>

          <a-form
            :form="form"
            class="login-form"
            @submit="handleSubmit"
          >
            <!-- 用户名 -->
            <a-form-item class="login-field-item">
              <label class="login-label">用户名</label>

              <div class="login-input-wrap">
                <a-icon type="user" class="login-input-icon" />

                <a-input
                  v-decorator="[
                    'username',
                    {
                      initialValue: '',
                      rules: [
                        {
                          required: true,
                          whitespace: true,
                          message: '请输入用户名',
                        },
                      ],
                    },
                  ]"
                  class="login-input"
                  placeholder="请输入用户名"
                  autocomplete="username"
                  size="large"
                  allow-clear
                />
              </div>
            </a-form-item>

            <!-- 密码 -->
            <a-form-item class="login-field-item">
              <label class="login-label">密码</label>

              <div class="login-input-wrap">
                <a-icon type="lock" class="login-input-icon" />

                <a-input-password
                  v-decorator="[
                    'password',
                    {
                      initialValue: '',
                      rules: [
                        {
                          required: true,
                          message: '请输入密码',
                        },
                      ],
                    },
                  ]"
                  class="login-input"
                  placeholder="请输入密码"
                  autocomplete="current-password"
                  size="large"
                />
              </div>
            </a-form-item>

            <!-- 登录按钮 -->
            <a-form-item class="login-submit-item">
              <a-button
                type="primary"
                html-type="submit"
                class="login-submit-btn"
                :loading="loading"
                block
              >
                <span>登录</span>
                <a-icon type="arrow-right" class="submit-arrow" />
              </a-button>
            </a-form-item>
          </a-form>

          <div class="login-security-tip">
            <a-icon type="safety-certificate" />
            <span>安全登录 · 高效管理</span>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { login, getUserMenus } from '@/api/auth'
import { normalizeRouters } from '@/router/asyncRouter'

export default {
  name: 'Login',

  data() {
    return {
      loading: false,
      form: this.$form.createForm(this),
    }
  },

  computed: {
    systemName() {
      return (
        this.$store.state.setting.systemName ||
        '电竞直播管理后台'
      )
    },
  },

  created() {
    if (this.$store.state.account.token) {
      this.$router.replace('/home')
    }
  },

  methods: {
    ...mapMutations({
      setToken: 'account/setToken',
      setExpireTime: 'account/setExpireTime',
      setUser: 'account/setUser',
      setPermissions: 'account/setPermissions',
      setRoles: 'account/setRoles',
      setTheme: 'setting/setTheme',
      setLayout: 'setting/setLayout',
      setMultipage: 'setting/setMultipage',
      setRouter: 'setting/setRouter',
      fixSiderbar: 'setting/fixSiderbar',
      fixHeader: 'setting/fixHeader',
      setColor: 'setting/setColor',
    }),

    saveLoginData(data) {
      this.setToken(data.token)

      this.setExpireTime(
        data.exipreTime ||
        data.expireTime ||
        data.expireAt
      )

      this.setUser(data.user || {})
      this.setPermissions(data.permissions || [])
      this.setRoles(data.roles || [])

      const config = data.config || {}

      if (config.theme) {
        this.setTheme(config.theme)
      }

      if (config.layout) {
        this.setLayout(config.layout)
      }

      if (config.multiPage !== undefined) {
        this.setMultipage(config.multiPage === '1')
      }

      if (config.fixSiderbar !== undefined) {
        this.fixSiderbar(config.fixSiderbar === '1')
      }

      if (config.fixHeader !== undefined) {
        this.fixHeader(config.fixHeader === '1')
      }

      if (config.color) {
        this.setColor(config.color)
      }
    },

    async loadMenus(username) {
      try {
        const res = await getUserMenus(username)
        this.setRouter(normalizeRouters(res))
      } catch (e) {
        this.setRouter(normalizeRouters(null))
      }
    },

    handleSubmit(e) {
      e.preventDefault()

      if (this.loading) return

      this.form.validateFields(async (err, values) => {
        if (err || this.loading) return

        this.loading = true

        try {
          const r = await login(
            values.username,
            values.password
          )

          if (!r || !r.data || r.data.code !== 200) {
            this.$message.warning(
              (r && r.data && r.data.msg) || '登录失败'
            )
            return
          }

          const data = r.data.data || {}

          this.saveLoginData(data)

          const username =
            (data.user && data.user.username) ||
            values.username

          await this.loadMenus(username)

          this.$message.success('登录成功')
          this.$router.push('/')
        } catch (error) {
          // 请求错误由项目已有的统一请求拦截器处理
        } finally {
          this.loading = false
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
/*
 * 电竞直播管理后台登录页
 * 设计版心：1920px
 * 兼容 Vue 2 + Ant Design Vue 1.x + Less 3
 * 不使用 CSS min() / max() / clamp()
 */

@login-blue: #2865f5;
@login-blue-light: #4387ff;
@login-text: #17243d;
@login-muted: #8995a9;
@login-border: #d6deea;

*,
*::before,
*::after {
  box-sizing: border-box;
}

.login-page {
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: hidden;
  color: @login-text;
  background:
    radial-gradient(
      ellipse at 14% 32%,
      rgba(112, 177, 255, 0.2) 0,
      rgba(112, 177, 255, 0.07) 28%,
      transparent 56%
    ),
    linear-gradient(
      120deg,
      #edf5ff 0%,
      #f8fbff 50%,
      #ffffff 100%
    );
}

/*
 * 1920px 设计版心：
 * 大屏下只保持版心最大 1920px，左右居中。
 * 外层背景继续铺满屏幕，避免超宽屏出现空白色块。
 */
.login-canvas {
  position: relative;
  width: 100%;
  max-width: 1920px;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  overflow: hidden;
  isolation: isolate;
}

/* 背景图层：使用独立的背景图片 */
.login-background {
  position: absolute;
  z-index: -2;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  // background-color: #f5f9ff;
  // background-image: url('/static/img/login-background.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

/* 左侧视觉区域 */
.login-visual {
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;
  left: 0;
  width: 61%;
  overflow: hidden;
  pointer-events: none;
}

.visual-glow {
  position: absolute;
  top: 12%;
  left: -12%;
  width: 88%;
  height: 72%;
  border-radius: 50%;
  background: radial-gradient(
    ellipse,
    rgba(69, 147, 255, 0.15) 0%,
    rgba(125, 190, 255, 0.07) 43%,
    transparent 73%
  );
}

/*
 * 科技图形：第一张图片
 * 使用 contain，保持原始比例，不拉伸。
 */
.visual-art {
  position: absolute;
  top: 50%;
  left: 2%;
  width: 94%;
  height: 88%;
  transform: translateY(-50%);
  background-image: url('/static/img/login-visual-art.png');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

/* 登录表单区域 */
.login-panel {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42%;
  height: 100%;
  min-height: 600px;
  padding: 48px 5.2% 72px 2%;
}

.login-content {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
}

.login-title {
  margin: 0 0 54px;
  color: @login-text;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 1px;
  overflow-wrap: anywhere;
}

/* 表单整体 */
.login-form {
  width: 100%;

  /deep/ .ant-form-item {
    margin-bottom: 34px;
  }

  /deep/ .ant-form-item-control {
    line-height: normal;
  }

  /deep/ .ant-form-explain {
    margin-top: 5px;
    font-size: 13px;
  }
}

.login-label {
  display: block;
  margin-bottom: 13px;
  color: #17243d;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
}

/* 输入框容器 */
.login-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  height: 60px;
  padding: 0 18px;
  border: 1px solid @login-border;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 2px 6px rgba(37, 70, 120, 0.025);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.login-input-wrap:focus-within {
  border-color: @login-blue;
  box-shadow: 0 0 0 3px rgba(40, 101, 245, 0.09);
}

.login-input-icon {
  flex: 0 0 auto;
  margin-right: 15px;
  color: #61718d;
  font-size: 21px;
}

/* Ant Design Vue 输入框 */
.login-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  color: @login-text;
  font-size: 17px;
  background: transparent;
  border: 0;
  box-shadow: none !important;

  /deep/ input {
    height: 100%;
    padding: 0;
    color: @login-text;
    font-size: 17px;
    background: transparent;
    border: 0;
    box-shadow: none !important;
  }

  /deep/ input::placeholder {
    color: #9aa6b9;
  }

  /deep/ .ant-input {
    border: 0;
    box-shadow: none !important;
  }

  /deep/ .ant-input-suffix {
    right: 0;
  }
}

/* 密码输入框 */
.login-input.ant-input-password {
  display: flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent;
}

.login-input.ant-input-password /deep/ .ant-input {
  height: 100%;
  padding: 0;
  background: transparent;
}

.login-input.ant-input-password /deep/ .ant-input-suffix {
  // position: static;
  margin-left: 10px;
}

/* 登录按钮 */
.login-submit-item {
  margin-top: 44px;
  margin-bottom: 0 !important;
}

.login-submit-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 62px;
  padding: 0 24px;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 3px;
  border: 0;
  border-radius: 11px;
  background: linear-gradient(
    105deg,
    #347cff 0%,
    #2459ef 100%
  );
  box-shadow: 0 12px 26px rgba(42, 101, 243, 0.17);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease,
    filter 0.2s ease;
}

.login-submit-btn:hover,
.login-submit-btn:focus {
  color: #fff;
  background: linear-gradient(
    105deg,
    #4387ff 0%,
    #3268f5 100%
  );
  box-shadow: 0 14px 30px rgba(42, 101, 243, 0.24);
  transform: translateY(-1px);
}

.login-submit-btn:active {
  transform: translateY(0);
}

.login-submit-btn > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.submit-arrow {
  position: absolute;
  right: 24px;
  font-size: 17px;
  opacity: 0;
  transform: translateX(-5px);
  transition: all 0.2s ease;
}

.login-submit-btn:hover .submit-arrow {
  opacity: 1;
  transform: translateX(0);
}

/* 安全提示 */
.login-security-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  color: #94a2b8;
  font-size: 13px;
  letter-spacing: 1px;
}

.login-security-tip .anticon {
  margin-right: 7px;
  color: #6c9df9;
}

/* 底部辅助文字 */
.login-footer {
  position: absolute;
  right: 5.2%;
  bottom: 5.5%;
  display: flex;
  align-items: center;
  color: #8a9ab4;
  font-size: 13px;
  letter-spacing: 1px;
  white-space: nowrap;
}

.footer-dot {
  width: 6px;
  height: 6px;
  margin-right: 10px;
  border-radius: 50%;
  background: #347cff;
}

.footer-line {
  width: 42px;
  height: 1px;
  margin-right: 12px;
  background: #a9c7f8;
}

/*
 * 超宽屏：
 * 登录界面版心不超过 1920px。
 * 4K 屏幕两侧保留页面背景，不放大表单与科技图形。
 */
@media screen and (min-width: 1921px) {
  .login-canvas {
    max-width: 1920px;
  }
}

/* 中小型桌面 */
@media screen and (max-width: 1440px) {
  .login-visual {
    width: 60%;
  }

  .visual-art {
    left: 0;
    width: 100%;
    height: 80%;
  }

  .login-panel {
    width: 43%;
    padding-right: 4%;
    padding-left: 2%;
  }

  .login-content {
    max-width: 400px;
  }

  .login-title {
    margin-bottom: 40px;
    font-size: 32px;
  }

  .login-form /deep/ .ant-form-item {
    margin-bottom: 28px;
  }

  .login-label {
    margin-bottom: 10px;
    font-size: 16px;
  }

  .login-input-wrap {
    height: 54px;
    padding: 0 15px;
    border-radius: 9px;
  }

  .login-input,
  .login-input /deep/ input {
    font-size: 15px;
  }

  .login-input-icon {
    font-size: 19px;
  }

  .login-submit-item {
    margin-top: 34px;
  }

  .login-submit-btn {
    height: 56px;
    font-size: 18px;
  }

  .login-footer {
    right: 4%;
    font-size: 11px;
  }
}

/* 平板横屏、窄窗口 */
@media screen and (max-width: 1024px) and (min-width: 641px) {
  .login-visual {
    width: 53%;
  }

  .visual-art {
    left: -8%;
    width: 112%;
    height: 72%;
  }

  .login-panel {
    width: 50%;
    padding-right: 4%;
    padding-left: 2%;
  }

  .login-content {
    max-width: 360px;
  }

  .login-title {
    margin-bottom: 32px;
    font-size: 29px;
  }

  .login-form /deep/ .ant-form-item {
    margin-bottom: 24px;
  }

  .login-input-wrap {
    height: 52px;
  }

  .login-submit-item {
    margin-top: 28px;
  }

  .login-submit-btn {
    height: 52px;
  }

  .login-footer {
    display: none;
  }
}

/* 手机竖屏 */
@media screen and (max-width: 640px) {
  .login-page {
    overflow-x: hidden;
    overflow-y: auto;
    background:
      radial-gradient(
        ellipse at 50% 0%,
        rgba(91, 160, 255, 0.15) 0,
        transparent 55%
      ),
      linear-gradient(145deg, #eef6ff 0%, #fff 100%);
  }

  .login-canvas {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 28px 24px;
    overflow: hidden;
  }

  .login-background {
    background-position: center;
    background-size: cover;
  }

  .login-visual {
    position: relative;
    top: auto;
    bottom: auto;
    left: auto;
    width: 100%;
    height: 190px;
    flex: 0 0 190px;
  }

  .visual-glow {
    top: 0;
    left: 5%;
    width: 90%;
    height: 100%;
  }

  .visual-art {
    top: 50%;
    left: 0;
    width: 100%;
    height: 100%;
    transform: translateY(-50%);
  }

  .login-panel {
    position: relative;
    top: auto;
    right: auto;
    display: block;
    width: 100%;
    height: auto;
    min-height: 0;
    padding: 0;
  }

  .login-content {
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  .login-title {
    margin-bottom: 32px;
    font-size: 27px;
    text-align: center;
  }

  .login-form /deep/ .ant-form-item {
    margin-bottom: 24px;
  }

  .login-label {
    margin-bottom: 9px;
    font-size: 15px;
  }

  .login-input-wrap {
    height: 52px;
    padding: 0 13px;
  }

  .login-input,
  .login-input /deep/ input {
    font-size: 15px;
  }

  .login-submit-item {
    margin-top: 30px;
  }

  .login-submit-btn {
    height: 52px;
    font-size: 17px;
  }

  .login-security-tip {
    margin-top: 18px;
  }

  .login-footer {
    display: none;
  }
}

/* 矮屏设备：缩小间距，尽量避免登录按钮被挤出视口 */
@media screen and (max-height: 760px) and (min-width: 641px) {
  .login-panel {
    padding-top: 20px;
    padding-bottom: 46px;
  }

  .login-title {
    margin-bottom: 26px;
    font-size: 30px;
  }

  .login-form /deep/ .ant-form-item {
    margin-bottom: 20px;
  }

  .login-submit-item {
    margin-top: 24px;
  }

  .login-input-wrap {
    height: 50px;
  }

  .login-submit-btn {
    height: 52px;
  }

  .login-security-tip {
    margin-top: 14px;
  }

  .login-footer {
    bottom: 2%;
  }
}

/* 自动填充时，整个输入框铺满背景色 */
.login-input-wrap:has(input:-webkit-autofill) {
  background-color: #e7f0ff;
}

/* 统一 Chrome / Edge 自动填充区域的颜色 */
.login-input-wrap /deep/ input:-webkit-autofill,
.login-input-wrap /deep/ input:-webkit-autofill:hover,
.login-input-wrap /deep/ input:-webkit-autofill:focus,
.login-input-wrap /deep/ input:-webkit-autofill:active {
  -webkit-text-fill-color: @login-text !important;
  caret-color: @login-text;
  -webkit-box-shadow: 0 0 0 1000px #e7f0ff inset !important;
  box-shadow: 0 0 0 1000px #e7f0ff inset !important;
  transition: background-color 99999s ease-out 0s;
}
</style>
